---
title: Hospitality vs. residential
date: 2026-02-14
author: Brian Ellison
category: Practice
excerpt: They look like the same discipline. They are not. A note on what hospitality demands that residential does not, and vice versa.
placeholder: true
---

People assume hospitality and residential are the same practice with different floor plans.

They are not. They are different disciplines that happen to share a vocabulary.

## What hospitality demands

A hospitality interior has to **survive its occupants**. The floors will be walked on by twelve thousand pairs of shoes per year. The banquettes will be sat in by every kind of body, including some that have had three negronis. The bar will be wiped down twice a night for ten years.

That changes everything. **Specifications become questions about endurance**, not just about beauty. Walnut becomes solid walnut, not veneer. Leather becomes contract-grade. Lighting fixtures become serviceable. Floor finishes become refinishable. Corner details become inevitable, because corners get hit.

It also changes the **light plan**. Hospitality light is **scenographic** — designed for an arc through an evening, from arrival to departure. A room that looks beautiful at 7pm but glares at 11pm has failed. Dimmers everywhere is not an option in hospitality; it is the design.

## What residential demands

A residential interior has to **become specific** to the people who live in it. The same plaster that anchors a hospitality room can feel cold in a kitchen if you don't know the family eats breakfast there together every morning at 6:30am.

Residential demands extended listening. The studio does not specify a single fixture for a private residence without understanding how the family **uses** the room. Where do they read? Who reads where? Is the morning light worth waking for, or is it a problem to be filtered?

The objects matter less than the choreography of the day they support.

<div class="pull-quote">

"Hospitality is composed for thousands of strangers. Residential is composed for two people and the people they love. The vocabulary is the same. The sentences are not."

</div>

## What carries across

The shared discipline is **restraint**.

A room over-decorated will fail in either context. In hospitality it will feel anxious and inauthentic. In residential it will feel cluttered and rented.

Both demand the same posture: fewer, better, considered. Materials chosen like words. Light treated as a building element. Detail at the edges, not the center.

*A practice note. More to follow.*
